import { useState } from 'react';

import WorkOrderStatusPill from '@atom/components/common/workOrderDetail/workOrderStatusPill/WorkOrderStatusPill';
import WorkPriorityIcon from '@atom/components/common/WorkPriorityIcon';
import { useNavigation } from '@atom/hooks/useNavigation';
import { Icon, IconButton, Progress } from '@atom/mui';
import {
  CloseIcon,
  ErrorOutlineIcon,
  type IconComponent,
  InfoIcon,
  PlaylistAddCheckIcon,
  SearchOffIcon,
  SupervisorAccountIcon,
} from '@atom/mui/icons';
import { WorkOrderDetailType } from '@atom/types/work';
import userUtilities from '@atom/utilities/userUtilities';
import { showWorkDateTime } from '@atom/utilities/workPreferenceUtilities';

import SidebarMessage from '../shared/SidebarMessage';
import { getSummaryImage } from '../shared/sidebarUtilities';
import TabNav from '../shared/TabNav';

import WorkInfoTab from './WorkInfoTab';
import WorkTasksTab from './WorkTasksTab';
import WorkTeamTab from './WorkTeamTab';

import sidebarClasses from '../shared/sidebars.module.css';
import classes from './workOrder.module.css';

const TABS: { value: string; icon: IconComponent }[] = [
  { value: 'info', icon: InfoIcon },
  { value: 'tasks', icon: PlaylistAddCheckIcon },
  { value: 'team', icon: SupervisorAccountIcon },
];

export type WorkOrderSidebarViewProps = {
  workOrder: WorkOrderDetailType | undefined;
  loading?: boolean;
  error?: boolean;
  onClose: () => void;
};

const WorkOrderSidebarView = ({
  workOrder,
  loading,
  error,
  onClose,
}: WorkOrderSidebarViewProps) => {
  const [activeTab, setActiveTab] = useState<string>('info');
  const [, { Link }] = useNavigation();

  if (loading) {
    return (
      <div className={sidebarClasses.spinnerContainer}>
        <Progress />
      </div>
    );
  }

  if (error) {
    return (
      <SidebarMessage
        icon={ErrorOutlineIcon}
        message="Something went wrong loading this work order."
        onClose={onClose}
        variant="error"
      />
    );
  }

  if (!workOrder) {
    return (
      <SidebarMessage
        icon={SearchOffIcon}
        message="This work order was not found."
        onClose={onClose}
      />
    );
  }

  const teamUsers = userUtilities.rollUpTaskUsers(workOrder.tasks);

  const imageUrl = getSummaryImage(
    workOrder.mainPhotoFileId,
    workOrder.location,
    180,
  );

  const getBody = () => {
    switch (activeTab) {
      case 'info':
        return <WorkInfoTab work={workOrder} />;
      case 'tasks':
        return (
          <WorkTasksTab
            tasks={workOrder.tasks}
            showDateTime={showWorkDateTime(workOrder)}
          />
        );
      case 'team':
        return <WorkTeamTab users={teamUsers} />;
      default:
        return null;
    }
  };

  const body = getBody();

  return (
    <div className={sidebarClasses.container}>
      <div className={sidebarClasses.header}>
        <div className={sidebarClasses.headerTitleRow}>
          <div className={sidebarClasses.headerTitle}>{workOrder.name}</div>
          <div className={sidebarClasses.headerActions}>
            <IconButton onClick={onClose}>
              <Icon icon={CloseIcon} />
            </IconButton>
          </div>
        </div>
        <div className={classes.priorityStatusRow}>
          <WorkPriorityIcon priorityId={workOrder.priorityId} />
          <WorkOrderStatusPill
            statusId={workOrder.statusId}
            className="dashboard-map-list"
          />
        </div>
      </div>
      <div className={sidebarClasses.trayContentContainer}>
        <div
          className={sidebarClasses.summaryImage}
          style={{ backgroundImage: `url('${imageUrl}')` }}
        />
        <TabNav tabs={TABS} active={activeTab} onSelect={setActiveTab} />
        {body}
      </div>
      <Link to={`/workOrders/${workOrder.id}`}>
        <div className={sidebarClasses.viewDetailButton}>View Full Detail</div>
      </Link>
    </div>
  );
};

export default WorkOrderSidebarView;
